// ============================================================================
// Dialysis chemical stock — the data layer. 2026-09-23.
//
// Every number these screens show is a CALCULATION in the database over the
// permanent movement log (stock_movements). Nothing here computes a stock level,
// a rate or a cost; it reads the views and calls the stock_* functions.
//
// ⛔ Every write goes through commitWrite (PLAYBOOK §15): the function returns
// the rows it wrote, and zero rows is a failure, never a silent no-op.
// ⛔ Every loader registers against every table it reads, joined ones included
// (TCSD-0248) — READS below is that list, and every hook uses it.
// ============================================================================

import { supabase } from '../../../shared/supabase';
import { commitWrite } from '../../../shared/save-path';
import type { Database } from '../../../types/database.types';

type Views = Database['public']['Views'];
type Tables = Database['public']['Tables'];
type Fns = Database['public']['Functions'];

export type ClinicStockRow = Views['v_dialysis_chem_clinic_stock']['Row'];
export type LocationStockRow = Views['v_stock_location_stock']['Row'];
export type MovementRow = Views['v_stock_movements_effective']['Row'];
export type DeliveryCostRow = Views['v_dialysis_chem_delivery_cost']['Row'];
export type PoLineRow = Views['v_stock_po_lines']['Row'];
export type CompanyRateRow = Views['v_dialysis_chem_company_rate']['Row'];
export type ProductRow = Views['v_dialysis_chem_products']['Row'];
export type CorrectionRow = Tables['stock_movements']['Row'];
export type OfficeRow = Pick<
  Tables['office_locations']['Row'],
  'office_id' | 'office_name' | 'office_region' | 'stock_role'
>;

/** Every table any stock screen reads, directly or through a view. */
export const READS = [
  'stock_movements',
  'stock_settings',
  'facility_chemicals',
  'purchase_orders',
  'purchase_order_lines',
  'office_locations',
  'facilities',
  'facility_details',
  'accounts',
  'markets',
  'supplies',
  'vendor_supply_pricing',
  'service_assignments',
] as const;

/** What every stock write touches, so every stock screen re-reads. */
const TOUCHES = [
  'stock_movements',
  'stock_settings',
  'facility_chemicals',
  'purchase_orders',
  'purchase_order_lines',
];

// Micro-Chem is ALWAYS gallons; Stearns is ALWAYS tabs (decision 8).
export const MICRO_CHEM = 'SUP-0212';
export const STEARNS = 'SUP-0230';

async function must<T>(p: PromiseLike<{ data: unknown; error: unknown }>, what: string): Promise<T> {
  const { data, error } = await p;
  if (error) {
    const e = new Error(`Could not load ${what}.`);
    (e as Error & { cause?: unknown }).cause = error;
    throw e;
  }
  return (data ?? []) as T;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function fetchClinicStock(filter?: { facilityId?: string }): Promise<ClinicStockRow[]> {
  let q = supabase.from('v_dialysis_chem_clinic_stock').select('*');
  if (filter?.facilityId) q = q.eq('facility_id', filter.facilityId);
  return must(q.order('facility_name').order('supply_id'), 'the clinics’ chemical stock');
}

export function fetchLocationStock(): Promise<LocationStockRow[]> {
  return must(
    supabase.from('v_stock_location_stock').select('*').order('office_id').order('supply_id'),
    'stock at each location',
  );
}

export function fetchMovements(filter: { facilityId?: string; officeId?: string }): Promise<MovementRow[]> {
  let q = supabase.from('v_stock_movements_effective').select('*');
  if (filter.facilityId) q = q.eq('facility_id', filter.facilityId);
  if (filter.officeId) q = q.or(`office_id.eq.${filter.officeId},to_office_id.eq.${filter.officeId}`);
  return must(q.order('occurred_at', { ascending: false }).limit(200), 'the stock history');
}

/** The corrections themselves, so history can say "Corrected by Troy: 23 → 30 — miscounted". */
export function fetchCorrections(ids: string[]): Promise<CorrectionRow[]> {
  if (ids.length === 0) return Promise.resolve([]);
  return must(
    supabase
      .from('stock_movements')
      .select('*')
      .eq('kind', 'correction')
      .in('corrects_movement_id', ids)
      .order('recorded_at', { ascending: true }),
    'the corrections',
  );
}

export function fetchDeliveryCosts(): Promise<DeliveryCostRow[]> {
  return must(
    supabase.from('v_dialysis_chem_delivery_cost').select('*').order('occurred_at', { ascending: false }),
    'chemical cost',
  );
}

export function fetchStockPoLines(poId?: string): Promise<PoLineRow[]> {
  let q = supabase.from('v_stock_po_lines').select('*');
  if (poId) q = q.eq('po_id', poId);
  return must(q.order('line_id'), 'the order lines');
}

export function fetchCompanyRates(): Promise<CompanyRateRow[]> {
  return must(supabase.from('v_dialysis_chem_company_rate').select('*'), 'the company starting rate');
}

export function fetchProducts(): Promise<ProductRow[]> {
  return must(supabase.from('v_dialysis_chem_products').select('*').order('supply_id'), 'the chemicals');
}

export function fetchStockOffices(): Promise<OfficeRow[]> {
  return must(
    supabase
      .from('office_locations')
      .select('office_id, office_name, office_region, stock_role')
      .neq('stock_role', 'none')
      .order('office_id'),
    'the stock locations',
  );
}

/** Dialysis revenue per month for the in-scope clinics (v_finance_book), for the cost share. */
export async function fetchDialysisRevenue(facilityIds: string[]): Promise<number | null> {
  if (facilityIds.length === 0) return null;
  const rows = await must<Array<{ revenue: number | null }>>(
    supabase.from('v_finance_book').select('facility_id, revenue').in('facility_id', facilityIds),
    'dialysis revenue',
  );
  if (rows.length === 0) return null;
  return rows.reduce((s, r) => s + Number(r.revenue ?? 0), 0);
}

/** Names for everyone who recorded an entry. v_employee_picker is the declared picker view. */
export async function fetchPeopleNames(
  eids: Array<string | null | undefined>,
): Promise<Record<string, { name: string; role: string | null }>> {
  const ids = Array.from(new Set(eids.filter((x): x is string => !!x)));
  if (ids.length === 0) return {};
  const rows = await must<
    Array<{
      employee_id: string | null;
      employee_name_first: string | null;
      employee_name_last: string | null;
      display_name: string | null;
      app_role: string | null;
    }>
  >(
    supabase
      .from('v_employee_picker')
      .select('employee_id, employee_name_first, employee_name_last, display_name, app_role')
      .in('employee_id', ids),
    'the names of the people who logged stock',
  );
  const out: Record<string, { name: string; role: string | null }> = {};
  for (const r of rows) {
    if (!r.employee_id) continue;
    const name =
      [r.employee_name_first, r.employee_name_last].filter(Boolean).join(' ').trim() ||
      r.display_name ||
      r.employee_id;
    out[r.employee_id] = { name, role: r.app_role };
  }
  return out;
}

/** Is this building a dialysis clinic whose customer supplies its own chemicals? */
export async function fetchCustomerSupplied(facilityId: string): Promise<{ dialysis: boolean; customerSupplied: boolean }> {
  const { data: fac, error } = await supabase
    .from('facilities')
    .select('facility_type, account_id')
    .eq('facility_id', facilityId)
    .maybeSingle();
  if (error) throw error;
  const f = fac as { facility_type: string | null; account_id: string | null } | null;
  if (!f || f.facility_type !== 'Dialysis') return { dialysis: false, customerSupplied: false };
  if (!f.account_id) return { dialysis: true, customerSupplied: false };
  const { data: acct, error: aErr } = await supabase
    .from('accounts')
    .select('supplies_own_chemicals')
    .eq('account_id', f.account_id)
    .maybeSingle();
  if (aErr) throw aErr;
  return {
    dialysis: true,
    customerSupplied: !!(acct as { supplies_own_chemicals: boolean } | null)?.supplies_own_chemicals,
  };
}

/** The clinic's non-standard TCS chemical rows — "phasing out" until the first delivery picks them up. */
export async function fetchPhasingOut(facilityId: string): Promise<Array<{ fc_id: string; name: string }>> {
  const rows = await must<
    Array<{ fc_id: string; supply_id: string | null; supply_name: string | null; managed_by: string | null }>
  >(
    supabase
      .from('facility_chemicals')
      .select('fc_id, supply_id, supply_name, managed_by')
      .eq('facility_id', facilityId)
      .eq('is_deleted', false)
      .neq('source', 'Dialysis_Standard'),
    'the chemicals being phased out',
  );
  const keep = rows.filter((r) => r.managed_by !== 'Facility');
  const ids = keep.map((r) => r.supply_id).filter((x): x is string => !!x);
  const names: Record<string, string> = {};
  if (ids.length > 0) {
    const sup = await must<Array<{ supply_id: string; item_name: string | null }>>(
      supabase.from('supplies').select('supply_id, item_name').in('supply_id', ids),
      'chemical names',
    );
    for (const s of sup) names[s.supply_id] = s.item_name ?? s.supply_id;
  }
  return keep.map((r) => ({
    fc_id: r.fc_id,
    name: (r.supply_id ? names[r.supply_id] : null) ?? r.supply_name ?? r.fc_id,
  }));
}

/** How many old (non-standard, TCS-supplied) chemical rows each clinic still lists. */
export async function fetchPhasingOutCounts(facilityIds: string[]): Promise<Record<string, number>> {
  if (facilityIds.length === 0) return {};
  const rows = await must<Array<{ facility_id: string; managed_by: string | null }>>(
    supabase
      .from('facility_chemicals')
      .select('facility_id, managed_by')
      .in('facility_id', facilityIds)
      .eq('is_deleted', false)
      .neq('source', 'Dialysis_Standard'),
    'the chemicals being phased out',
  );
  const out: Record<string, number> = {};
  for (const r of rows) {
    if (r.managed_by === 'Facility') continue;
    out[r.facility_id] = (out[r.facility_id] ?? 0) + 1;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Writes — each one is a stock_* function, and each returns the rows it wrote.
// ⛔ An empty optional input is sent as `null`, never `undefined` (TCSD-0382):
// `undefined` drops the key from the request, and a database input with no
// default then matches no function at all ("Could not find the function …").
// Every call here was checked against the live inputs on 2026-10-02.
// ---------------------------------------------------------------------------

export interface QtyLine {
  supply_id: string;
  qty: number;
}

export type CountSource = 'self' | 'reported' | 'inspection';

type Json = Fns['stock_log_count']['Args']['p_counts'];
const asJson = (v: unknown) => v as Json;

export function logCount(args: {
  facilityId?: string | null;
  officeId?: string | null;
  counts: QtyLine[];
  source: CountSource;
  workerId?: string | null;
  providerId?: string | null;
  reporterName?: string | null;
  occurredAt?: string | null;
  notes?: string | null;
}) {
  return commitWrite({
    label: 'save the count',
    write: supabase.rpc('stock_log_count', {
      p_facility_id: args.facilityId ?? null,
      p_office_id: args.officeId ?? null,
      p_counts: asJson(args.counts),
      p_source: args.source,
      p_worker_id: args.workerId ?? null,
      p_provider_id: args.providerId ?? null,
      p_reporter_name: args.reporterName ?? null,
      p_occurred_at: args.occurredAt ?? null,
      p_notes: args.notes ?? null,
    } as unknown as Fns['stock_log_count']['Args']),
    touches: TOUCHES,
    expect: 'at-least-one',
  });
}

export function logDelivery(args: {
  facilityId: string;
  officeId: string;
  counts: QtyLine[];
  leaves: QtyLine[];
  sdsPosted?: boolean | null;
  oldPickedUp?: boolean | null;
  bottlesLabeled?: number | null;
  dayStopId?: string | null;
  occurredAt?: string | null;
  notes?: string | null;
}) {
  return commitWrite({
    label: 'log the delivery',
    write: supabase.rpc('stock_log_delivery', {
      p_facility_id: args.facilityId,
      p_office_id: args.officeId,
      p_counts: asJson(args.counts),
      p_leaves: asJson(args.leaves),
      p_sds_posted: args.sdsPosted ?? null,
      p_old_chemicals_picked_up: args.oldPickedUp ?? null,
      p_bottles_labeled: args.bottlesLabeled ?? null,
      p_day_stop_id: args.dayStopId ?? null,
      p_occurred_at: args.occurredAt ?? null,
      p_notes: args.notes ?? null,
    } as unknown as Fns['stock_log_delivery']['Args']),
    touches: TOUCHES,
    expect: 'at-least-one',
  });
}

export function correctEntry(movementId: string, qty: number, reason: string) {
  return commitWrite({
    label: 'correct that number',
    write: supabase.rpc('stock_correct', { p_movement_id: movementId, p_qty: qty, p_reason: reason }),
    touches: TOUCHES,
  });
}

export function transferStock(args: { from: string; to: string; supplyId: string; qty: number; occurredAt?: string | null; notes?: string | null }) {
  return commitWrite({
    label: 'move the stock',
    write: supabase.rpc('stock_transfer', {
      p_from_office_id: args.from,
      p_to_office_id: args.to,
      p_supply_id: args.supplyId,
      p_qty: args.qty,
      p_occurred_at: args.occurredAt ?? null,
      p_notes: args.notes ?? null,
    } as unknown as Fns['stock_transfer']['Args']),
    touches: TOUCHES,
  });
}

export function pickupStock(args: { facilityId: string; officeId: string; supplyId: string; qty: number; occurredAt?: string | null; notes?: string | null }) {
  return commitWrite({
    label: 'record the pickup',
    write: supabase.rpc('stock_pickup', {
      p_facility_id: args.facilityId,
      p_office_id: args.officeId,
      p_supply_id: args.supplyId,
      p_qty: args.qty,
      p_occurred_at: args.occurredAt ?? null,
      p_notes: args.notes ?? null,
    } as unknown as Fns['stock_pickup']['Args']),
    touches: TOUCHES,
  });
}

export function receiveLine(args: { lineId: string; cases: number; receivedOn?: string | null; notes?: string | null }) {
  return commitWrite({
    label: 'confirm what arrived',
    write: supabase.rpc('stock_receive_line', {
      p_line_id: args.lineId,
      p_received_cases: args.cases,
      p_received_on: args.receivedOn ?? null,
      p_notes: args.notes ?? null,
    } as unknown as Fns['stock_receive_line']['Args']),
    touches: TOUCHES,
  });
}

export interface PurchaseLineDraft {
  supply_id: string;
  office_id: string;
  cases: number;
  case_price: number;
}

/** A stock purchase is entered once, when it has arrived (TCSD-0387). po_date is the invoice date. */
export interface PurchaseHeaderDraft {
  vendor_id: string;
  po_date: string;
  arrived_on: string;
  shipping: number | null;
  tax: number | null;
  invoice_number: string | null;
  payment_method: string | null;
  notes: string | null;
}

export function recordPurchase(po: PurchaseHeaderDraft, lines: PurchaseLineDraft[]) {
  return commitWrite({
    label: 'record the purchase',
    write: supabase.rpc('stock_record_purchase', { p_po: asJson(po), p_lines: asJson(lines) }),
    touches: TOUCHES,
  });
}

/** Edit an order after saving — freight, tax, dates, invoice number. Costs recompute on read. */
export function updatePurchase(
  poId: string,
  patch: Partial<Pick<Tables['purchase_orders']['Row'], 'shipping' | 'tax' | 'expected_delivery' | 'invoice_number' | 'status' | 'notes' | 'subtotal' | 'total'>>,
) {
  return commitWrite({
    label: 'save the order',
    write: supabase.from('purchase_orders').update(patch).eq('po_id', poId).select('po_id'),
    touches: TOUCHES,
  });
}

/** Edit a line before it is received — cases, price, where it ships. After receipt, correct the received quantity instead. */
export function updatePurchaseLine(
  lineId: string,
  patch: Partial<Pick<Tables['purchase_order_lines']['Row'], 'qty' | 'unit_price' | 'extended_price' | 'surplus_storage_location_id'>>,
) {
  return commitWrite({
    label: 'save the order line',
    write: supabase.from('purchase_order_lines').update(patch).eq('line_id', lineId).select('line_id'),
    touches: TOUCHES,
  });
}

export type SettingKey =
  | 'rate_per_chair_night'
  | 'usage_per_month'
  | 'deliver_at_days'
  | 'fill_to_days'
  | 'reorder_buffer_days'
  | 'stock_office';

export function setSetting(args: {
  scope: 'company' | 'office' | 'facility';
  officeId?: string | null;
  facilityId?: string | null;
  supplyId?: string | null;
  key: SettingKey;
  value?: number | null;
  valueOfficeId?: string | null;
  until?: string | null;
  reason?: string | null;
  clear?: boolean;
}) {
  return commitWrite({
    label: args.clear ? 'return it to automatic' : 'save the setting',
    write: supabase.rpc('stock_set_setting', {
      p_scope: args.scope,
      p_office_id: args.officeId ?? null,
      p_facility_id: args.facilityId ?? null,
      p_supply_id: args.supplyId ?? null,
      p_key: args.key,
      p_value: args.value ?? null,
      p_value_office_id: args.valueOfficeId ?? null,
      p_until: args.until ?? null,
      p_reason: args.reason ?? null,
      p_clear: args.clear ?? false,
    } as unknown as Fns['stock_set_setting']['Args']),
    touches: TOUCHES,
  });
}
